import { useEffect, useState } from "react";

export type Theme = "system" | "light" | "dark";

export function getStoredTheme(): Theme {
  if (typeof window === "undefined") return "system";
  const val = localStorage.getItem("bwj-theme");
  if (val === "light" || val === "dark" || val === "system") return val;
  return "system";
}

export function getEffectiveTheme(theme: Theme): "light" | "dark" {
  if (typeof window === "undefined") return "light";
  if (theme === "system") {
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  }
  return theme;
}

export function applyTheme(theme: Theme) {
  if (typeof window === "undefined") return;
  const effective = getEffectiveTheme(theme);
  const root = document.documentElement;

  if (effective === "dark") {
    root.classList.add("dark");
    root.setAttribute("data-theme", "dark");
    root.style.colorScheme = "dark";
  } else {
    root.classList.remove("dark");
    root.setAttribute("data-theme", "light");
    root.style.colorScheme = "light";
  }
}

export function setStoredTheme(theme: Theme) {
  if (typeof window === "undefined") return;
  localStorage.setItem("bwj-theme", theme);
  applyTheme(theme);
  window.dispatchEvent(new CustomEvent("bwj-theme-change", { detail: theme }));
}

export function useTheme() {
  const [theme, setThemeState] = useState<Theme>("system");
  const [effective, setEffective] = useState<"light" | "dark">("light");

  useEffect(() => {
    const current = getStoredTheme();
    setThemeState(current);
    setEffective(getEffectiveTheme(current));
    applyTheme(current);

    const onThemeChange = (e: Event) => {
      const next = (e as CustomEvent<Theme>).detail || getStoredTheme();
      setThemeState(next);
      setEffective(getEffectiveTheme(next));
    };

    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const onSystemChange = () => {
      if (getStoredTheme() === "system") {
        applyTheme("system");
        setEffective(getEffectiveTheme("system"));
      }
    };

    window.addEventListener("bwj-theme-change", onThemeChange);
    media.addEventListener("change", onSystemChange);

    return () => {
      window.removeEventListener("bwj-theme-change", onThemeChange);
      media.removeEventListener("change", onSystemChange);
    };
  }, []);

  const changeTheme = (next: Theme) => {
    setStoredTheme(next);
    setThemeState(next);
    setEffective(getEffectiveTheme(next));
  };

  const cycleTheme = () => {
    // Intuitive toggle: if currently dark -> switch to light, if currently light -> switch to dark
    const next: Theme = effective === "dark" ? "light" : "dark";
    changeTheme(next);
  };

  return { theme, effective, changeTheme, cycleTheme };
}
